<template>
    <!-- PRODUCT LD+JSON-TAG -->
</template>
<script lang="ts" setup>
    import type {
        WithContext,
        ProductGroup as ProductGroupSchema,
        Product as ProductSchema,
        Offer as ProductOffer,
        Review as ProductReview,
        AggregateRating as ProductAggregateRating,
        MerchantReturnPolicy,
        OfferShippingDetails,
        ItemAvailability,
        ImageObject
    } from 'schema-dts';
    import type {
        ReviewsRawResponse,
        Product,
        Sku,
        FullImage
    } from '~/types';
    import { ProductAvailabilityStatus } from '~/types';
    import {
        useFullImage,
        useSkusUtils
    } from '~/composables';
    import { pathJoin } from 'x1-base/utils';
    import { isError as lo_isError } from 'es-toolkit';

    type GenerateTagProps = {
        reviewObj: Nullable<ReviewsRawResponse>;
    };

    type ShippingProfile = {
        id: string;
        handlingDays: number;
    };

    const SHIPPING_STOCK: ShippingProfile = {
        id: '#shipping_details_stock',
        handlingDays: 2
    };

    const SHIPPING_MADE_TO_ORDER: ShippingProfile = {
        id: '#shipping_details_custom',
        handlingDays: 5
    };

    const props = defineProps<{
        product: Product;
    }>();

    const strOutput = useState<Nullable<string>>('keyProdJsonLdtTag', () => null);

    useHead(() => {
        if (!strOutput.value) {
            return {};
        }

        return {
            script: [
                {
                    type: 'application/ld+json',
                    children: strOutput.value
                }
            ]
        };
    });

    const route = useRoute();
    const $config = useRuntimeConfig();
    const {
        title: brandTitle,
        fqdn
    } = $config.public.brand;
    const { getSingleImageUrlFromFullImage } = useFullImage();
    const { getColorOptFromSkuOptUids } = useSkusUtils();
    const { getReviews } = useReview();

    await callOnce(async () => {
        // console.time('generate-tag');
        const reviewsResp = await getReviews({}, { page_size: 1 });
        const reviewObj: Nullable<ReviewsRawResponse> = (!lo_isError(reviewsResp)) ? reviewsResp : null;
        strOutput.value = generateTag({ reviewObj });
        // console.timeEnd('generate-tag');
    });

    function generateTag(options: GenerateTagProps): Nullable<string> {
        const {
            description,
            tagline: slogan,
            name,
            uid: productUid,
            path: productPath
        } = props.product;

        const hasVariant = getSkus();

        const productGroup: WithContext<ProductGroupSchema> = {
            '@context': 'https://schema.org',
            '@type': 'ProductGroup',
            url: productPath ? `https://${pathJoin(fqdn, productPath)}` : '',
            productGroupID: productUid,
            name,
            description,
            slogan,
            hasVariant,
            brand: {
                '@type': 'Brand',
                name: brandTitle
            },
            // TODO: This probably should be computed
            variesBy: [
                // 'https://schema.org/size',
                'https://schema.org/color'
            ]
        };

        const groupImages = getGroupImages();

        if (groupImages.length) {
            productGroup.image = groupImages;
        }

        const aggregateRating = getAggregateRatingObj(options.reviewObj);

        if (aggregateRating) {
            productGroup.aggregateRating = aggregateRating;
        }

        const review = getReviewObj(options.reviewObj);

        if (review) {
            productGroup.review = review;
        }

        // Emit only the shipping nodes an offer actually points at, so a
        // stock-only product doesn't advertise a made-to-order lead time.
        const usedProfiles = [SHIPPING_STOCK, SHIPPING_MADE_TO_ORDER]
            .filter((profile: ShippingProfile) => {
                return props.product.skus.some((sku: Sku) => getShippingProfile(sku).id === profile.id);
            });

        const jsonLdObj: (
            WithContext<ProductGroupSchema>
            | WithContext<MerchantReturnPolicy>
            | WithContext<OfferShippingDetails>
        )[] = [
            productGroup,
            getReturnPolicyObj(),
            ...usedProfiles.map(getShippingDetailsObj)
        ];

        try {
            return JSON.stringify(jsonLdObj);
        } catch (error) {
            console.info('Failed to stringify JSON LD OBJ');
            return null;
        }
    }

    // ProductGroup needs its own `image`; Product carries none, so borrow from
    // a sku, preferring the default one.
    //
    // Selection has to test the images a sku actually yields, not how many it
    // carries. Built-to-order skus hold placeholder entries that have alt text
    // but a null url — six of them on the default sku of a custom head — so a
    // sku can report images and still produce nothing renderable.
    function getGroupImages(): ImageObject[] {
        const { skus, defaultSkuUid } = props.product;
        const defaultSku = skus.find((sku: Sku) => sku.uid === defaultSkuUid);
        const ordered = defaultSku
            ? [defaultSku, ...skus.filter((sku: Sku) => sku !== defaultSku)]
            : skus;

        for (const sku of ordered) {
            const images = sku.images?.length ? getImages(sku.images) : [];

            if (images.length) {
                return images;
            }
        }

        return [];
    }

    function getSkus(): ProductSchema[] {
        const variants: ProductSchema[] = props.product.skus.map((sku: Sku) => {
            return getSkuItem(sku);
        });

        return variants;
    }

    function getSkuItem(sku: Sku): ProductSchema {
        const hasMerchantReturnPolicy = {
            '@id': '#return_policy'
        };

        const shippingDetails = {
            '@id': getShippingProfile(sku).id
        };

        const offers: ProductOffer = {
            '@type': 'Offer',
            category: sku.categoryName,
            url: sku.path ? `https://${pathJoin(fqdn, sku.path)}` : '',
            priceCurrency: 'USD',
            price: sku.price,
            availability: getItemPropStatus(sku.status),
            hasMerchantReturnPolicy,
            shippingDetails
        };

        // An absent upc must stay absent — `${null}` yields the string 'null',
        // which Google reads as a malformed GTIN.
        if (sku.upc) {
            offers.gtin = `${sku.upc}`;
        }

        if (sku.isOnSale) {
            offers.price = sku.userPrice;
            offers.priceValidUntil = '2029-12-31';
        }

        const obj: ProductSchema = {
            '@type': 'Product',
            name: sku.seoTitle ?? sku.name,
            description: sku.detail || props.product.description,
            sku: sku.itemNo,
            color: sku.colorName ?? tryGetColor(sku),
            offers,
            hasMerchantReturnPolicy
        };

        const images = sku.images?.length ? getImages(sku.images) : [];

        // Only emit `image` when it has entries: an empty array reads to Google
        // as a missing required field.
        if (images.length) {
            obj.image = images;
        }

        return obj;
    }
    
    // Shipping is live-rated at exact carrier cost, so there is no honest
    // constant for `shippingRate` — actual US rates run $0.54-$262.16 and 48%
    // of orders ship free. A fabricated rate risks the merchant listing when
    // Google reconciles it against checkout, so the property is omitted and
    // only the destination and timing are declared.
    //
    // The free-shipping thresholds ($99 US / $199 CA-MX / $299 rest) have no
    // home here: `freeShippingThreshold` appears nowhere in Google's merchant
    // listing or shipping policy docs. They belong on an organization-level
    // ShippingService node instead.
    //
    // Build-to-order skus take materially longer to leave the building than
    // stock (2.22 vs 0.82 days on average), so they get their own node rather
    // than one blended promise that over-states custom and under-sells stock.
    function getShippingDetailsObj(shipping: ShippingProfile): WithContext<OfferShippingDetails> {
        return {
            '@context': 'https://schema.org',
            '@type': 'OfferShippingDetails',
            '@id': shipping.id,
            shippingDestination: {
                '@type': 'DefinedRegion',
                addressCountry: 'US'
            },
            deliveryTime: {
                '@type': 'ShippingDeliveryTime',
                handlingTime: {
                    '@type': 'QuantitativeValue',
                    minValue: 0,
                    maxValue: shipping.handlingDays,
                    unitCode: 'DAY'
                },
                transitTime: {
                    '@type': 'QuantitativeValue',
                    minValue: 1,
                    maxValue: 7,
                    unitCode: 'DAY'
                }
            }
        };
    }

    // Only narrow to the faster promise when the sku is positively known to be
    // stock. An absent `requiresAssemblyOrder` means the API predates the field
    // — unknown, so it falls to the slower profile rather than over-promising.
    function getShippingProfile(sku: Sku): ShippingProfile {
        return (sku.requiresAssemblyOrder === false)
            ? SHIPPING_STOCK
            : SHIPPING_MADE_TO_ORDER;
    }

    function getReturnPolicyObj(): WithContext<MerchantReturnPolicy> {
        return {
            '@context': 'https://schema.org',
            '@type': 'MerchantReturnPolicy',
            '@id': '#return_policy',
            applicableCountry: 'US',
            returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
            merchantReturnDays: 30,
            returnMethod: 'https://schema.org/ReturnByMail',
            returnFees: 'https://schema.org/FreeReturn'
        }
    }

    function getItemPropStatus(status: Nullable<ProductAvailabilityStatus>): ItemAvailability {
        switch (status) {
            case ProductAvailabilityStatus.Preorder:
            case ProductAvailabilityStatus.ComingSoon:
                return 'PreOrder';
            case ProductAvailabilityStatus.SoldOut:
                return 'OutOfStock';
            case ProductAvailabilityStatus.Backorder:
                return 'BackOrder';
        }

        return 'InStock';
    }

    function tryGetColor(sku: Sku): string {
        let color: string = '';
        const { optionGroups } = props.product;
        const { optionUids } = sku;

        if (optionGroups && optionUids) {
            const colorOpt = getColorOptFromSkuOptUids(optionGroups, optionUids);
            color = colorOpt?.value.name || '';
        }

        return color;
    }

    // `aggregateRating` is not a property of Review — it belongs on the item
    // being reviewed. Nesting it inside the review is what Google reports as
    // "You rated a review, rather than an item".
    function getAggregateRatingObj(respObj: Nullable<ReviewsRawResponse>): Nullable<ProductAggregateRating> {
        if (!respObj) {
            return null;
        }

        const [, summary] = respObj;

        if (!summary.total) {
            return null;
        }

        return {
            '@type': 'AggregateRating',
            reviewCount: summary.total,
            ratingValue: summary.average
        };
    }

    function getReviewObj(respObj: Nullable<ReviewsRawResponse>): Nullable<ProductReview> {
        if (!respObj) {
            return null;
        }

        const [reviewsObj] = respObj;
        const revItem = reviewsObj.data?.[0];

        // A bare `{'@type': 'Review'}` has neither a rating nor an author, so
        // it fails validation — omit the node entirely instead.
        if (!revItem) {
            return null;
        }

        return {
            '@type': 'Review',
            reviewRating: {
                '@type': 'Rating',
                ratingValue: revItem.rating
            },
            author: {
                '@type': 'Person',
                name: revItem.author
            }
        };
    }

    function getImages(images: FullImage[]): ImageObject[] {
        return images.reduce((acc: ImageObject[], fullImg: FullImage) => {
            if (fullImg.url) {
                acc.push({
                    '@type': 'ImageObject',
                    caption: fullImg.alt,
                    contentUrl: fullImg.url
                });
            }

            return acc;
        }, []);
    }
</script>

<style lang="scss"></style>
